import { Grid } from '@mui/material';
import Spinner from 'components/shared/Spinner';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import PaymentMethodType from 'store/enums/PaymentMethodType';
import { PaymentFormValues } from 'store/types/FormValues';
import { ConfigContext } from 'components/ConfigGuard';
import { NmiPayments, PaymentChangeEvent } from '@nmipayments/nmi-pay-react';
import { Appearance } from '@nmipayments/nmi-core';
import CollectJSResponse from 'store/types/CollectJSResponse';

interface PaymentMethodFormProps {
  disabled?: boolean;
  type?: PaymentMethodType;
  onPaymentComplete: (response: CollectJSResponse) => void;
  onMethodValidChange: (valid: boolean) => void;
  country?: string;
  subsidiaryId?: string;
  onChange?: (values: Partial<PaymentFormValues>) => void | null;
}

const nmiPaymentFormAppearance: Appearance = {
  theme: 'light',
  customTheme: {
    layout: {
      radius: {
        small: '4px',
        medium: '8px',
        large: '12px',
      },
      borderWidth: {
        small: '1px',
        medium: '1px',
        large: '4px',
      },
      spacing: {
        padding: '16px',
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        '2xl': '48px',
        '3xl': '64px',
      },
    },
    typography: {
      size: {
        xs: '16px',
        sm: '18px',
        md: '20px',
        lg: '22px',
        xl: '24px',
        '2xl': '26px',
      },
      weight: {
        light: '300',
        medium: '500',
        bold: '700',
      },
      font: {
        sans: 'Work Sans, -apple-system, BlinkMacSystemFont, sans-serif',
        mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      },
    },
  },
  layoutSpacing: 'spacious',
};

const PaymentMethodForm: React.FunctionComponent<PaymentMethodFormProps> = ({
  disabled = false,
  onMethodValidChange,
  type = PaymentMethodType.Card,
  subsidiaryId,
  onChange,
}) => {
  const [formValid, setFormValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [publicKey, setPublicKey] = useState<string>('');
  const { paymentConfiguration } = useContext(ConfigContext);

  useEffect(() => {
    onMethodValidChange(formValid);
  }, [formValid, onMethodValidChange]);

  useEffect(() => {
    const subsidiaryPublicKey = paymentConfiguration?.solupayItems?.find((i) => i.subsidiaryId === subsidiaryId);
    setPublicKey(subsidiaryPublicKey?.publicKey ?? paymentConfiguration?.solupay?.publicKey ?? '');
  }, [subsidiaryId, paymentConfiguration]);

  const paymentFormOnChange = useCallback(
    (data: PaymentChangeEvent) => {
      setFormValid(data.complete ?? false);

      if (onChange && data.complete) {
        onChange({ paymentMethodValid: true });
        if (data.lookupData?.card || data.lookupData?.check) {
          onChange({ card: data.lookupData.card, check: data.lookupData?.check, token: data.token });
        }
      }
    },
    [onChange]
  );

  const paymentFormOnLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  return (
    <Spinner loading={loading} transparent={false}>
      <Grid>
        {!disabled && (
          <NmiPayments
            tokenizationKey={publicKey}
            paymentMethods={type === PaymentMethodType.Card ? ['card'] : ['ach']}
            appearance={nmiPaymentFormAppearance}
            onChange={paymentFormOnChange}
            onFieldsAvailable={paymentFormOnLoaded}
          />
        )}
      </Grid>
    </Spinner>
  );
};
export default PaymentMethodForm;
